import React, { useState, useRef, useEffect } from "react";
import CommonButton from "../elments/CommonButton";

const PasteModal = ({ 
  isOpen, 
  onClose, 
  onPaste, 
  onAction, 
  label,
  focusedRowIndex,
  initialContent = "",
  preserveOriginalIndices = false,
  onPreserveIndicesChange
}) => {
  const [pasteText, setPasteText] = useState(initialContent);
  const [localPreserveIndices, setLocalPreserveIndices] = useState(preserveOriginalIndices);
  const editableRef = useRef(null);

  useEffect(() => {
    if (initialContent && editableRef.current) {
      editableRef.current.innerHTML = initialContent;
      setPasteText(initialContent);
    }
  }, [initialContent]);

  useEffect(() => {
    if (editableRef.current && editableRef.current.innerHTML !== pasteText) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      if (editableRef.current.innerHTML !== pasteText) {
        editableRef.current.innerHTML = pasteText;

        if (range && editableRef.current.contains(range.startContainer)) {
          try {
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            const newRange = document.createRange();
            newRange.selectNodeContents(editableRef.current);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    }
  }, [pasteText]);

  const handlePaste = (e) => {
    e.preventDefault();

    const htmlContent = e.clipboardData.getData("text/html");
    const plainContent = e.clipboardData.getData("text/plain");

    let cleaned;
    if (htmlContent) {
      cleaned = cleanPastedHTML(htmlContent);
    } else {
      cleaned = plainContent
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line)
        .map((line) => `<div>${line}</div>`)
        .join("");
    }

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cleaned;

      const frag = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        frag.appendChild(tempDiv.firstChild);
      }

      range.insertNode(frag);
      range.collapse(false);

      setPasteText(editableRef.current.innerHTML);
      onPaste(editableRef.current.innerHTML);
    }
  };

  const handleAction = (mode) => {
    // Pass the preserve indices state to the parent
    onAction(mode, localPreserveIndices);
  };

  const handlePreserveIndicesChange = (e) => {
    const newValue = e.target.checked;
    setLocalPreserveIndices(newValue);
    if (onPreserveIndicesChange) {
      onPreserveIndicesChange(newValue);
    }
  };

  const cleanPastedHTML = (dirty) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    const allowedTags = [
      "B", "I", "U", "STRONG", "EM", "BR", "P", "DIV", "OL", "UL", "LI", "SPAN",
    ];

    const allowedStyles = [
      "color", "background-color", "font-weight", "font-style", "text-decoration",
    ];

    const cleanNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent
          .replace(/\u00A0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        return text || "";
      }

      if (["TABLE", "TBODY", "TR", "TD", "TH"].includes(node.nodeName)) {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .filter((text) => text.trim())
          .join(" ")
          .replace(/\s{2,}/g, " ")
          .trim();
        return content;
      }

      if (node.nodeName === "OL" || node.nodeName === "UL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      if (node.nodeName === "LI") {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .join("")
          .trim();
        return content ? `<div>${content}</div>` : "";
      }

      if (
        node.nodeName === "SPAN" &&
        (!node.innerHTML.trim() || node.classList.contains("MsoNormal"))
      ) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      if (
        node.nodeType === Node.COMMENT_NODE ||
        node.nodeType === Node.PROCESSING_INSTRUCTION_NODE ||
        node.nodeName === "STYLE" ||
        node.nodeName === "SCRIPT" ||
        node.nodeName === "META"
      ) {
        return "";
      }

      if (!allowedTags.includes(node.nodeName)) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      if (node.hasAttribute("class")) {
        const classes = node.getAttribute("class").split(" ");
        const wordClasses = ["MsoNormal", "MsoListParagraph", "MsoBodyText"];
        const filteredClasses = classes.filter(
          (cls) => !wordClasses.includes(cls)
        );
        if (filteredClasses.length > 0) {
          node.setAttribute("class", filteredClasses.join(" "));
        } else {
          node.removeAttribute("class");
        }
      }

      const wordAttributes = ["lang", "style", "type", "start"];
      wordAttributes.forEach((attr) => {
        if (node.hasAttribute(attr)) {
          node.removeAttribute(attr);
        }
      });

      if (node.style && node.style.length > 0) {
        const styles = [];
        allowedStyles.forEach((styleProp) => {
          const value = node.style.getPropertyValue(styleProp);
          if (value) styles.push(`${styleProp}: ${value}`);
        });
        if (styles.length > 0) {
          node.setAttribute("style", styles.join("; "));
        } else {
          node.removeAttribute("style");
        }
      }

      const childContent = Array.from(node.childNodes)
        .map(cleanNode)
        .filter((text) => text.trim())
        .join("");

      if (!childContent.trim()) {
        return "";
      }

      if (["P", "DIV"].includes(node.nodeName) && childContent.trim()) {
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      if (
        ["B", "I", "U", "STRONG", "EM", "SPAN"].includes(node.nodeName) &&
        childContent.trim()
      ) {
        if (node.nodeName === "SPAN" && !node.getAttribute("style")) {
          return childContent;
        }
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      return childContent;
    };

    let cleanedContent = Array.from(doc.body.childNodes)
      .map(cleanNode)
      .filter((text) => text.trim())
      .join("");

    if (!cleanedContent.includes("<div>") && !cleanedContent.includes("<li>")) {
      const lines = cleanedContent.split(/\n/);
      const processedLines = lines
        .map((line) => {
          line = line.replace(/^\s*\d+\.\s*/, "").trim();
          line = line.replace(/^[•\-]\s*/, "").trim();
          return line;
        })
        .filter((line) => line.trim());

      if (processedLines.length > 1) {
        cleanedContent = processedLines
          .map((line) => `<div>${line}</div>`)
          .join("");
      }
    }

    cleanedContent = cleanedContent
      .replace(/\n\s*\n/g, "\n")
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br><br>")
      .trim();

    return cleanedContent || "";
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "8px",
          width: "600px",
          maxWidth: "90%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 style={{ margin: "0 0 15px 0" }}>Paste {label}</h4>

        <div
          ref={editableRef}
          contentEditable
          onInput={(e) => {
            setPasteText(e.currentTarget.innerHTML);
            onPaste(e.currentTarget.innerHTML);
          }}
          onPaste={handlePaste}
          style={{
            width: "100%",
            minHeight: "150px",
            padding: "12px",
            fontSize: "14px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            overflow: "auto",
            outline: "none",
            marginBottom: "15px",
          }}
          data-placeholder={`Paste your ${label.toLowerCase()} here...`}
        />

        {/* Preserve Original Indices Checkbox */}
        <div style={{ 
          marginBottom: "15px",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          border: "1px solid #e9ecef"
        }}>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            color: "#495057"
          }}>
            <input
              type="checkbox"
              checked={localPreserveIndices}
              onChange={handlePreserveIndicesChange}
              style={{ cursor: "pointer" }}
            />
            <span>Preserve original indices from pasted items</span>
            
          </label>
          <div style={{ 
            fontSize: "11px", 
            color: "#6c757d", 
            marginTop: "5px",
            paddingLeft: "24px"
          }}>

          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <CommonButton 
              label="Prepend" 
              title="Add items before all existing items" 
              onClick={() => handleAction("prepend")} 
              variant="tab"
            />
            <CommonButton 
              label="Append" 
              title="Add items after all existing items" 
              onClick={() => handleAction("append")} 
              variant="tab"
            />
            <CommonButton 
              label="Insert Before" 
              title="Insert items before selected row" 
              onClick={() => handleAction("insertBefore")} 
              variant="tab"
              disabled={focusedRowIndex === null}
            />
            <CommonButton 
              label="Insert After" 
              title="Insert items after selected row" 
              onClick={() => handleAction("insertAfter")} 
              variant="tab"
              disabled={focusedRowIndex === null}
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <CommonButton 
              label="Replace" 
              title="Replace all existing items" 
              onClick={() => handleAction("replace")} 
              variant="tab"
            />
            <CommonButton 
              label="Replace Current Row" 
              title="Replace Current Row" 
              onClick={() => handleAction("replacerc")} 
              variant="tab"
            />

            <CommonButton 
              label="Clear" 
              title="Clear the clipboard" 
              onClick={() => {
                setPasteText("");
                onPaste("");
              }} 
              variant="tab"
            />
            <CommonButton 
              label="Cancel" 
              title="Cancel" 
              onClick={onClose} 
              variant="danger"
            />
          </div>
        </div>
        
        {focusedRowIndex === null && (
          <div style={{ 
            marginTop: '10px', 
            color: '#ff9800', 
            fontSize: '12px',
            textAlign: 'center'
          }}>
            ⚠️ Insert Before/After options require clicking on a row first
          </div>
        )}
      </div>
    </div>
  );
};

export default PasteModal;